<template>
  <div data-cy="api-docs">
    <div v-if="loading" class="flex justify-center py-10">
      <Spinner />
    </div>

    <Alert
      v-else-if="notAvailable"
      type="info"
      data-cy="api-docs-not-available"
      text="API access isn't available on this account yet."
    />

    <Alert v-else-if="loadFailed" type="error" data-cy="api-docs-error">
      The docs couldn't be loaded.
      <button type="button" class="link ml-1" @click="load">Try again</button>
    </Alert>

    <div v-else-if="docs" class="grid gap-6 md:grid-cols-[12rem_1fr]">
      <nav aria-label="API docs pages" data-cy="api-docs-nav">
        <ul class="m-0 list-none space-y-1 p-0 text-sm">
          <li v-for="link in docs.data" :key="link.slug">
            <a
              :href="docsHref(link.slug)"
              class="block rounded px-2 py-1"
              :class="
                link.slug === currentSlug
                  ? 'bg-blue-50 font-semibold text-blue-700'
                  : 'text-gray-700 hover:bg-gray-100'
              "
              :aria-current="link.slug === currentSlug ? 'page' : undefined"
              :data-cy="`api-docs-nav:${link.slug}`"
              @click.prevent="goTo(link.slug)"
              >{{ link.title }}</a
            >
          </li>
        </ul>
        <p
          class="m-0 mt-4 px-2 text-xs text-gray-500"
          data-cy="api-docs-public"
        >
          The same docs are public, for developers without a Sera login, at
          <a
            :href="PUBLIC_DOCS_PATH"
            target="_blank"
            rel="noopener"
            class="link"
            data-cy="api-docs-public-link"
            >{{ PUBLIC_DOCS_PATH }}</a
          >, with an
          <a
            :href="`${PUBLIC_DOCS_PATH}/openapi.yaml`"
            target="_blank"
            rel="noopener"
            class="link"
            data-cy="api-docs-openapi-link"
            >OpenAPI spec</a
          >.
        </p>
      </nav>

      <div class="min-w-0">
        <section
          v-if="resourcesHere.length"
          class="mb-6 rounded-lg border border-gray-200 bg-white p-4 text-sm"
          data-cy="api-docs-try"
        >
          <h2 class="m-0 mb-1 text-base font-semibold text-gray-900">Try it</h2>
          <p class="m-0 mb-3 text-gray-600">
            Your base URL is
            <code data-cy="api-docs-base-url">{{ docs.meta.base_url }}</code
            >. Replace <code>{{ API_KEY_PLACEHOLDER }}</code> with one of your
            keys: Sera shows a key only once, when it's created.
          </p>
          <div
            v-for="resource in resourcesHere"
            :key="resource.name"
            class="border-t border-gray-100 py-3 first-of-type:border-t-0"
            :data-cy="`api-docs-resource:${resource.name}`"
          >
            <div class="mb-1 flex flex-wrap items-baseline gap-2">
              <span class="font-semibold text-gray-900">{{
                resource.label
              }}</span>
              <code class="text-gray-700">GET {{ resource.path }}</code>
              <span class="text-xs text-gray-500"
                >scope <code>{{ resource.scope }}</code></span
              >
            </div>
            <div class="relative rounded-lg bg-gray-50">
              <button
                type="button"
                class="absolute right-2 top-2 rounded px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
                :data-cy="`api-docs-copy:${resource.name}`"
                @click="copy(resource)"
              >
                {{ copied === resource.name ? "Copied" : "Copy" }}
              </button>
              <pre
                class="m-0 overflow-x-auto p-3 pr-16 font-mono text-xs text-gray-800"
                :data-cy="`api-docs-curl:${resource.name}`"
                >{{ curlFor(docs.meta.base_url, resource) }}</pre>
            </div>
          </div>
        </section>

        <div v-if="pageLoading" class="flex justify-center py-10">
          <Spinner />
        </div>
        <Alert
          v-else-if="pageFailed"
          type="error"
          data-cy="api-docs-page-error"
        >
          This page couldn't be loaded.
          <button type="button" class="link ml-1" @click="loadPage">
            Try again
          </button>
        </Alert>
        <!-- Rendered and sanitized on the server from the repo's Markdown. -->
        <!-- eslint-disable vue/no-v-html -->
        <article
          v-else-if="page"
          ref="articleRef"
          class="api-docs-page max-w-4xl overflow-x-auto text-sm text-gray-700"
          data-cy="api-docs-page"
          @click="onPageClick"
          v-html="page.html"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import Alert from "@/components/Seranova/Alert/Alert.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import type {
  ApiDocsIndex,
  ApiDocsPage,
  ApiResource
} from "@/vuex/modules/apiDocs";
import {
  API_ACCESS_PATH,
  API_KEY_PLACEHOLDER,
  apiKeyError,
  curlFor,
  docsHref
} from "./apiAccess";

const OVERVIEW = "overview";
// Served by Rails (DevelopersController), outside the SPA: open in a new tab.
const PUBLIC_DOCS_PATH = "/developers";

// Emitted once the first load has finished, whatever its outcome.
const emit = defineEmits<{ settled: [] }>();

const store = useStore();
const route = useRoute();
const router = useRouter();

const docs = ref<ApiDocsIndex | null>(null);
const loading = ref(false);
const loadFailed = ref(false);
// Every route answers 404 while API access is off for the account.
const notAvailable = ref(false);

const page = ref<ApiDocsPage | null>(null);
const pageLoading = ref(false);
const pageFailed = ref(false);
const articleRef = ref<HTMLElement | null>(null);
const copied = ref<string | null>(null);

const currentSlug = computed(() => {
  const requested = route.query.page;
  return docs.value?.data.some((link) => link.slug === requested)
    ? (requested as string)
    : OVERVIEW;
});

const section = computed(() =>
  typeof route.query.section === "string" ? route.query.section : null
);

// Every resource on the overview; on a resource's page, its own.
const resourcesHere = computed<ApiResource[]>(() => {
  const resources = docs.value?.meta.resources ?? [];
  return currentSlug.value === OVERVIEW
    ? resources
    : resources.filter((resource) => resource.page === currentSlug.value);
});

async function load() {
  loading.value = true;
  loadFailed.value = false;
  try {
    docs.value = await store.dispatch("fetchApiDocs");
  } catch (error) {
    if (apiKeyError(error, "").status === 404) notAvailable.value = true;
    else loadFailed.value = true;
  } finally {
    loading.value = false;
  }
}

async function loadPage() {
  pageLoading.value = true;
  pageFailed.value = false;
  try {
    page.value = await store.dispatch("fetchApiDocsPage", currentSlug.value);
  } catch {
    pageFailed.value = true;
  } finally {
    pageLoading.value = false;
  }
  await nextTick();
  scrollToSection();
}

function scrollToSection() {
  if (!section.value || !articleRef.value) return;
  const target = Array.from(
    articleRef.value.querySelectorAll<HTMLElement>("[id]")
  ).find((element) => element.id === section.value);
  target?.scrollIntoView?.({ behavior: "smooth", block: "start" });
}

function goTo(slug: string, sectionId: string | null = null) {
  const query: Record<string, string> = { tab: "docs", page: slug };
  if (sectionId) query.section = sectionId;
  router.replace({ query });
}

// Links between docs pages stay on this tab; in-page anchors scroll.
function onPageClick(event: MouseEvent) {
  const anchor = (event.target as HTMLElement).closest("a");
  const href = anchor?.getAttribute("href");
  if (!href) return;
  if (href.startsWith("#")) {
    event.preventDefault();
    goTo(currentSlug.value, href.slice(1));
  } else if (href.startsWith(`${API_ACCESS_PATH}?`)) {
    event.preventDefault();
    const query = new URLSearchParams(href.split("?")[1]);
    goTo(query.get("page") ?? OVERVIEW, query.get("section"));
  }
}

async function copy(resource: ApiResource) {
  if (!docs.value) return;
  try {
    await navigator.clipboard.writeText(
      curlFor(docs.value.meta.base_url, resource)
    );
    copied.value = resource.name;
    setTimeout(() => (copied.value = null), 2000);
  } catch {
    window.notify.error(
      "Couldn't copy. Select the command and copy it instead."
    );
  }
}

watch(currentSlug, (slug, previous) => {
  if (docs.value && slug !== previous) loadPage();
});

watch(section, () => scrollToSection());

onMounted(async () => {
  await load();
  if (docs.value) await loadPage();
  emit("settled");
});
</script>

<style scoped>
.api-docs-page :deep(h1) {
  @apply mb-3 mt-0 text-xl font-semibold text-gray-900;
}
.api-docs-page :deep(h2) {
  @apply mb-2 mt-6 scroll-mt-4 text-lg font-semibold text-gray-900;
}
.api-docs-page :deep(h3) {
  @apply mb-2 mt-4 scroll-mt-4 text-base font-semibold text-gray-900;
}
.api-docs-page :deep(p),
.api-docs-page :deep(ul),
.api-docs-page :deep(ol) {
  @apply mb-3;
}
.api-docs-page :deep(ul) {
  @apply list-disc pl-5;
}
.api-docs-page :deep(ol) {
  @apply list-decimal pl-5;
}
.api-docs-page :deep(a) {
  @apply text-blue-700 underline;
}
.api-docs-page :deep(code) {
  @apply rounded bg-gray-100 px-1 text-xs text-gray-800;
}
.api-docs-page :deep(pre) {
  @apply mb-3 overflow-x-auto rounded-lg bg-gray-50 p-3;
}
.api-docs-page :deep(pre code) {
  @apply bg-transparent p-0;
}
.api-docs-page :deep(li) {
  display: list-item;
  list-style: inherit;
}
/* The app's base styles blank list markers. */
.api-docs-page :deep(li::marker) {
  content: normal;
}
.api-docs-page :deep(table) {
  @apply mb-3 w-full text-left;
}
.api-docs-page :deep(th) {
  @apply bg-gray-50 px-2 py-1 text-xs font-semibold uppercase text-gray-500;
}
.api-docs-page :deep(td) {
  @apply whitespace-normal break-words border-b border-gray-100 px-2 py-1 align-top;
}
.api-docs-page :deep(td:first-child) {
  @apply whitespace-nowrap;
}
</style>
